import { Component, Inject, PLATFORM_ID, ViewChild, type OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe, isPlatformBrowser } from '@angular/common';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, type Sort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { combineLatest, concatMap, EMPTY, of, startWith } from 'rxjs';
import { addDays, format, isAfter, isSameYear, isWithinInterval, subDays, subMonths, subYears } from 'date-fns';

import { NotificationComponent } from '../../shared/components/notification/notification.component';
import { type AccountDetail } from '../../shared/models/account-detail';
import { StorageEnum } from '../../shared/enums/storage.enum';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AccountService } from '../../shared/services/account.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from '../../shared/services/storage.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { LiveAnnouncer } from '@angular/cdk/a11y';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from '../../shared/services/util.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from '../../shared/services/cms.service';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { BillingSettingsComponent } from '../../shared/components/billing-settings/billing-settings.component';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { PaymentService } from '../../shared/services/payment.service';
import { AutopayHistoryPipe } from '../pipes/autopay-history.pipe';
import { CreditPipe } from '../../shared/pipes/credit.pipe';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { Router, RouterModule } from '@angular/router';
import { HeadlineEnum } from '../../shared/enums/headline.enum';
import { SafePipe } from '../../shared/pipes/dom-sanitizer.pipe';
import { CustomerClassEnum } from '../../shared/enums/customerclass.enum';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { PageHeader } from '../../shared/models/page-header.model';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { PreviousRouteService } from '../../shared/services/previous-route.service';
import { Nav } from '../../shared/enums/nav.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { SeoService } from '../../shared/services/seo.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { NotificationsService } from '../../shared/services/notifications.service';

@Component({
  selector: 'app-history',
  providers: [CurrencyPipe, DatePipe],
  imports: [
    NotificationComponent, //
    ReactiveFormsModule,
    MatTableModule,
    DatePipe,
    CurrencyPipe,
    MatSortModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCardModule,
    MatButtonModule,
    MatPaginatorModule,
    CommonModule,
    BillingSettingsComponent,
    AutopayHistoryPipe,
    CreditPipe,
    SafePipe,
    PageHeaderComponent,
    RouterModule,
    NotificationComponent,
  ],
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss',
})
export class HistoryComponent implements OnInit {
  pageReady = false;
  pageLabels: any;
  formErrorMessage = '';
  accountDetail: AccountDetail;
  displayedColumns: string[] = ['ActivityDate', 'ActivityType', 'ActivityAmount'];
  timePeriods: any[] = [];
  activityTypes: Set<string> = new Set<string>();
  billingHistoryData: any[] = [];
  currentMonthsFetched: number = 0;
  isBrowser = false;
  dataSource: any = [];
  filterGroup: FormGroup;
  withinFourDays: boolean = false;
  noRecordsFoundText = '';
  closedAccount: boolean = false;
  seasonalAccount: boolean = false;
  billSettingsData: any = {
    showWallet: false,
  };

  acct: any;
  advertisementRightMessage: string = '';
  advertisementLeftImage: any;
  advertisementRedirectURL: string = '';
  advertisementButtonLabel: string = '';
  advertisementButtonAriaLabel: string = '';
  headerData = new PageHeader();
  pageName = 'History';
  seasonalSmlComReconnectCredits = '$75';

  @ViewChild(MatSort) sort: MatSort | undefined;
  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;

  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) readonly platformId: Object,
    private readonly _liveAnnouncer: LiveAnnouncer,
    private readonly account: AccountService,
    private readonly cms: CmsService,
    private readonly fb: FormBuilder,
    private readonly payment: PaymentService,
    private readonly storage: StorageService,
    private readonly utils: UtilService,
    private readonly router: Router,
    private readonly dateP: DatePipe,
    private readonly prev: PreviousRouteService,
    private readonly seoS: SeoService,
    private readonly notifi: NotificationsService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.accountDetail = this.storage.getSession(StorageEnum.AccountDetail);
    this.acct = this.storage.getSession(StorageEnum.LoginResponse);
    this.filterGroup = this.fb.group({
      ActivityType: [null],
      TimePeriod: [null],
    });
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      if (this.router.url.includes('seasonal')) {
        this.seasonalAccount = true;
      }
      if (this.router.url.includes('closed')) {
        this.closedAccount = true;
      }

      if (this.accountDetail) {
        this.payment.getPreferredPayments(this.accountDetail).subscribe((data: any) => {
          if (data.PaymentMethods?.length > 0) {
            this.billSettingsData.showWallet = true;
            this.billSettingsData = JSON.parse(JSON.stringify(this.billSettingsData));
          }
        });
      }
    }

    this.cms
      .getContent('oas-billing-history', 'OAS Billing History')
      .pipe(
        concatMap((data: any) => {
          this.pageLabels = this.cms.flattenBuilderReferencesPreservingKeys(data);

          this.billSettingsData.billingSettings = this.pageLabels.billingSettings;
          this.billSettingsData.header = this.pageLabels.billingSettingsHeader;
          this.timePeriods = data.timePeriods;
          this.seoS.setSeoTags(data.seo);

          if (this.isBrowser) {
            if (!this.seasonalAccount && !this.closedAccount) {
              this.headerData.Message = this.pageLabels.header;
              return of(null);
            } else {
              // Unfortunalty for Closed Seasonal accounts I need to Load Account Detail to get CustomerClass
              if (!this.prev.getPreviousUrl().includes(Nav.MultiAccountSummary)) {
                return this.account.getAccountDetail(this.acct.Accounts[0].AccountNumber, this.acct.UserName);
              } else {
                return of(this.storage.getSession(StorageEnum.AccountDetail));
              }
            }
          } else {
            return EMPTY;
          }
        }),
        concatMap((data) => {
          if (data) {
            this.accountDetail = data;
            this.storage.setSession(StorageEnum.AccountDetail, this.accountDetail);
            this.setHeadline();
          }

          // Default to 6 months initially
          this.utils.showSpinner();
          return this.account.billingHistory(this.acct.Accounts[0].AccountNumber, 6).pipe(
            concatMap((historyData) => {
              this.currentMonthsFetched = 6;

              // Set up filter observables after initial data load
              this.filterGroup.get('ActivityType')?.setValue(this.pageLabels.allActivityFilterText);
              this.filterGroup.get('TimePeriod')?.setValue(this.timePeriods[1]);

              return combineLatest([
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                this.filterGroup
                  .get('ActivityType')!
                  .valueChanges.pipe(startWith(this.pageLabels.allActivityFilterText)),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                this.filterGroup.get('TimePeriod')!.valueChanges.pipe(startWith(this.timePeriods[1])),
              ]).pipe(
                concatMap(([activityType, timePeriod]) => {
                  // Check if 'last year' is selected (valueType is neither 'days' nor 'months')
                  const isLastYear =
                    timePeriod?.valueType?.toLowerCase() !== 'days' &&
                    timePeriod?.valueType?.toLowerCase() !== 'months';
                  const requestedMonths = isLastYear ? 24 : timePeriod?.value || 6;

                  // Only call service if requested months is > 6 and <= 24 and > currentMonthsFetched
                  if (requestedMonths > 6 && requestedMonths <= 24 && requestedMonths > this.currentMonthsFetched) {
                    this.utils.showSpinner();
                    return this.account.billingHistory(this.acct.Accounts[0].AccountNumber, requestedMonths).pipe(
                      concatMap((newHistoryData) => {
                        this.currentMonthsFetched = requestedMonths;
                        return of({ historyData: newHistoryData, activityType, timePeriod, refetched: true });
                      }),
                    );
                  } else {
                    // Use existing data
                    return of({ historyData, activityType, timePeriod, refetched: false });
                  }
                }),
              );
            }),
          );
        }),
        concatMap((result: any) => {
          const { historyData, activityType, timePeriod, refetched } = result;

          // Process the service data (only clear if we refetched)
          if (refetched) {
            this.billingHistoryData = [];
          }

          if (refetched || this.billingHistoryData.length === 0) {
            if (!this.seasonalAccount && !this.closedAccount) {
              if (this.accountDetail.AutoPay?.RecurringScheduleId && (this.accountDetail.CurrentBalance ?? 0) > 0) {
                const ap = {
                  ActivityType: 'Payment',
                  DisplayActivity: this.pageLabels.autopayScheduledText,
                  ActivityAmount: this.accountDetail.CurrentBalance,
                  ActivityDate: addDays(new Date(), 1),
                  AutoPayment: true,
                };
                this.billingHistoryData.push(ap);
              }
            }

            this.billingHistoryData = this.billingHistoryData.concat(historyData);

            this.billingHistoryData.forEach((activity) => {
              activity.ActivityDate = new Date(activity.ActivityDate);
              switch (activity.ActivityType) {
                case 'Payment':
                  if (!activity.AutoPayment) {
                    activity.DisplayActivity = activity.ActivityType;
                  }
                  break;
                case 'Bill': {
                  const billAvailAfter = addDays(new Date(activity.ExtractedDate), 4);

                  if (isAfter(new Date(), billAvailAfter)) {
                    activity.DisplayActivity = `<a href="${activity.Url}" target="_blank" aria-label="${this.pageLabels.viewBillAriaText}">${this.pageLabels.viewBillCtaText}</a>`;
                  } else {
                    if (activity.ExtractedDate) {
                      activity.DisplayActivity = this.pageLabels.billAvailText;
                    } else {
                      activity.DisplayActivity = activity.ActivityType;
                    }
                  }
                  break;
                }
                default:
                  activity.DisplayActivity = activity.ActivityType;
                  break;
              }
            });

            this.activityTypes.clear();
            this.activityTypes.add(this.pageLabels.allActivityFilterText);
            this.billingHistoryData.forEach((activity: any) => {
              this.activityTypes.add(activity.ActivityType);
            });

            this.dataSource = new MatTableDataSource(this.billingHistoryData);
            let sortSet = false;
            let pagSet = false;
            const inter = setInterval(() => {
              if (this.sort && !sortSet) {
                this.dataSource.sort = this.sort;
                sortSet = true;
              }
              if (this.billingHistoryData.length > this.pageLabels.itemsPerPage) {
                if (this.paginator && !pagSet) {
                  this.dataSource.paginator = this.paginator;
                  pagSet = true;
                }
              } else {
                pagSet = true;
              }

              if (sortSet && pagSet) {
                clearInterval(inter);
              }
            }, 100);

            if (!this.pageReady) {
              this.pageReady = true;
            }
          }

          return of([activityType, timePeriod]);
        }),
      )
      .subscribe((data: any) => {
        // Run the filtering logic
        let typeFilter = '';
        let timeFilter: any = {};
        let oldestDate: any;
        let timeCondition: any;

        if (data.length > 0 && data[0]) {
          typeFilter = data[0];
        }

        if (data.length > 0 && data[1]) {
          timeFilter = data[1];
        }

        const dat = this.billingHistoryData.filter((hist) => {
          if (timeFilter.valueType?.toLowerCase() === 'days') {
            oldestDate = subDays(new Date(), timeFilter.value);
            timeCondition = isAfter(new Date(hist.ActivityDate), oldestDate);
          } else if (timeFilter.valueType?.toLowerCase() === 'months') {
            oldestDate = subMonths(new Date(), timeFilter.value);
            timeCondition = isAfter(new Date(hist.ActivityDate), oldestDate);
          } else {
            oldestDate = subYears(new Date(), 1);
            timeCondition = isSameYear(new Date(hist.ActivityDate), oldestDate);
          }

          if (typeFilter && typeFilter !== this.pageLabels.allActivityFilterText) {
            return timeCondition && hist.ActivityType === typeFilter;
          } else {
            return timeCondition;
          }
        });
        this.dataSource.data = dat;
        if (!this.seasonalAccount && !this.closedAccount) {
          this.withinFourDays = this.checkIsFourDayPrior();
        }
        this.noRecordsFoundText =
          dat.length > 0 ? '' : this.pageLabels.noRecordsFoundText.replace('{TimePeriod}', timeFilter.label);

        // Hide spinner after data is received, sorted and filtered
        this.utils.hideSpinner();
      });
  }

  setHeadline(): void {
    const headlineList = this.pageLabels.headlineList.headlineList;
    let headlineCode = HeadlineEnum.ClosedSeasonalResSMCom;

    const inInterval = isWithinInterval(new Date(this.acct.Accounts[0].ServiceAddresses[0].SaEndDate ?? ''), {
      start: subDays(new Date(), 90),
      end: new Date(),
    });

    if (this.accountDetail.CustomerClass === CustomerClassEnum.LRGCOM) {
      this.advertisementRedirectURL = this.pageLabels.signUpButton.url;
      this.advertisementButtonLabel = this.pageLabels.signUpButton.builderRefName;
      this.advertisementButtonAriaLabel = this.pageLabels.signUpButton.ariaLabel;
    }

    if (this.seasonalAccount) {
      this.advertisementLeftImage = this.pageLabels.advertisementSeasonalImage;
      if (
        this.accountDetail.CustomerClass === CustomerClassEnum.RES ||
        this.accountDetail.CustomerClass === CustomerClassEnum.SMLCOM
      ) {
        headlineCode = HeadlineEnum.ClosedSeasonalResSMCom;
        if (this.accountDetail.CustomerClass === CustomerClassEnum.SMLCOM) {
          this.pageLabels.advertisementClosedSeasonalResSmCom =
            this.pageLabels.advertisementClosedSeasonalResSmCom.replace('$25', this.seasonalSmlComReconnectCredits);
        }
        this.advertisementRightMessage = this.pageLabels.advertisementClosedSeasonalResSmCom;
        this.advertisementRedirectURL = this.pageLabels.reconnectButton.url;
        this.advertisementButtonLabel = this.pageLabels.reconnectButton.linkText;
        this.advertisementButtonAriaLabel = this.pageLabels.reconnectButton.ariaLabel;
      } else {
        headlineCode = HeadlineEnum.ClosedSeasonalLargeCom;
        this.advertisementRightMessage = this.pageLabels.advertisementClosedSeasonalLargeCom;
      }
    }

    if (this.closedAccount) {
      this.advertisementLeftImage = this.pageLabels.advertisementClosedImage;
      if (
        this.accountDetail.CustomerClass === CustomerClassEnum.RES ||
        this.accountDetail.CustomerClass === CustomerClassEnum.SMLCOM
      ) {
        if (inInterval) {
          headlineCode = HeadlineEnum.ClosedNotSeasonalResSMComReconnect;
          this.advertisementRightMessage = this.pageLabels.advertisementClosedNotSeasonalResSmComReconnect;
          this.advertisementRedirectURL = this.pageLabels.reconnectButton.url;
          this.advertisementButtonLabel = this.pageLabels.reconnectButton.linkText;
          this.advertisementButtonAriaLabel = this.pageLabels.reconnectButton.ariaLabel;
        } else {
          headlineCode = HeadlineEnum.ClosedNotSeasonalResSMComNoReconnect;
          this.advertisementRightMessage = this.pageLabels.advertisementClosedNotSeasonalResSmComNoReconnect;
        }
      } else {
        headlineCode = HeadlineEnum.ClosedNotSeasonalLargeCom;
        this.advertisementRightMessage = this.pageLabels.advertisementClosedNotSeasonalLargeCom;
      }
    }

    this.headerData.IsHeadline = true;
    const headlineRecord = headlineList.find((labels: any) => {
      return labels.headlineCode === headlineCode;
    });

    let customerName = this.acct.FirstName;
    if (!customerName) {
      customerName = this.acct.LastName;
    }
    this.headerData.Message = headlineRecord.headlineMessage
      .replace('{{ FirstName }}', customerName)
      .replace(
        '{{ SaEndDate }}',
        this.dateP.transform(this.acct.Accounts[0].ServiceAddresses[0].SaEndDate, 'MM/dd/yyyy'),
      );

    if (headlineRecord.ctaLink) {
      this.headerData.RedirectURL = headlineRecord.ctaLink.url;
      this.headerData.ButtonLabel = headlineRecord.ctaLink.builderRefName;
      this.headerData.ButtonAriaLabel = headlineRecord.ctaLink.ariaLabel;
    }
  }

  // async redirectFromHeadline(): Promise<void> {
  //   await this.router.navigateByUrl(this.headlineRedirectURL);
  // }

  async redirectFromAdvertisement(): Promise<void> {
    await this.router.navigateByUrl(this.advertisementRedirectURL);
  }

  redirectToWebsite(): void {
    window.location.href = this.advertisementRedirectURL;
  }

  /** Announce the change in sort state for assistive technology. */
  announceSortChange(sortState: Sort): void {
    // This example uses English messages. If your application supports
    // multiple language, you would internationalize these strings.
    // Furthermore, you can customize the message to add additional
    // details about the values being sorted.
    if (sortState.direction) {
      void this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      void this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  checkIsFourDayPrior(): boolean {
    const BillDueDate = this.accountDetail.Bills?.[0]?.ExtractedDate;
    const date = new Date();
    let showMessage: boolean = false;
    if (!this.utils.isNullOrUndefined(BillDueDate) && !this.utils.isNullOrUndefined(date) && BillDueDate) {
      const billAvailAfter = addDays(BillDueDate, 4);
      if (isAfter(date, billAvailAfter)) {
        showMessage = false;
      } else {
        showMessage = true;
        this.pageLabels.withinFourDaysText = this.pageLabels.withinFourDaysText.replaceAll(
          '{{ formatBillDueDate() }}',
          this.formatBillDueDate(),
        );
      }
    }
    return showMessage;
  }

  formatBillDueDate(): any {
    const BillDueDate = this.accountDetail.Bills?.[0]?.ExtractedDate;
    if (!this.utils.isNullOrUndefined(BillDueDate) && BillDueDate) {
      return format(addDays(BillDueDate, 5), 'MM/dd/yyyy');
    }
  }
}
